import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, X, Cloud, ShieldCheck, DownloadCloud, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

const STEP_META = [
  { key: "connect", icon: Cloud, title: "Connect a system", desc: "Link Salesforce, Zoho or Oracle to start.", to: "/dashboard/connections" },
  { key: "rule", icon: ShieldCheck, title: "Set a governance rule", desc: "Add a threshold so we can alert you on breaches.", to: "/dashboard/governance" },
  { key: "extraction", icon: DownloadCloud, title: "Run your first extraction", desc: "Export your data as CSV, Excel or JSON.", to: "/dashboard/extraction" },
];

export function GettingStartedChecklist() {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    const load = () => {
      api.get("/onboarding/status").then((r) => setStatus(r.data)).catch(() => {});
    };
    load();
    const onRefresh = () => load();
    window.addEventListener("gx-guide-refresh", onRefresh);
    window.addEventListener("focus", onRefresh);
    return () => {
      window.removeEventListener("gx-guide-refresh", onRefresh);
      window.removeEventListener("focus", onRefresh);
    };
  }, []);

  if (!status || status.dismissed || status.completed) return null;

  const steps = status.steps || {};
  const done = Object.values(steps).filter(Boolean).length;
  const total = STEP_META.length;
  const pct = Math.round((done / total) * 100);

  const dismiss = async () => {
    setStatus((s) => ({ ...s, dismissed: true }));
    try {
      await api.post("/onboarding/dismiss");
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="mb-8 overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 to-transparent" data-testid="getting-started-checklist">
      <div className="flex items-start justify-between gap-4 p-5">
        <div>
          <h3 className="font-display text-lg font-bold text-foreground">Getting started</h3>
          <p className="text-sm text-muted-foreground">Finish these {total} steps to get the most out of GovernX.</p>
        </div>
        <button
          onClick={dismiss}
          className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label="Dismiss checklist"
          data-testid="checklist-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="px-5">
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-border">
          <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
        <p className="mt-1.5 text-xs font-medium text-muted-foreground">{done} of {total} complete</p>
      </div>

      <div className="mt-4 grid gap-2 p-5 pt-2 sm:grid-cols-3">
        {STEP_META.map((s) => {
          const complete = !!steps[s.key];
          const next = !complete && STEP_META.find((x) => !steps[x.key])?.key === s.key;
          const Icon = s.icon;
          return (
            <Link
              key={s.key}
              to={s.to}
              className={`group flex flex-col gap-2 rounded-xl border p-4 transition-colors ${
                complete
                  ? "border-emerald-500/30 bg-emerald-500/5"
                  : next
                    ? "border-primary/50 bg-primary/5 ring-1 ring-primary/20"
                    : "border-border bg-card hover:border-primary/40"
              }`}
              data-testid={`checklist-step-${s.key}`}
            >
              <div className="flex items-center justify-between">
                <Icon className={`h-5 w-5 ${complete ? "text-emerald-600 dark:text-emerald-400" : "text-primary"}`} />
                {complete ? (
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" data-testid={`checklist-done-${s.key}`} />
                ) : (
                  <Circle className="h-5 w-5 text-muted-foreground/40" />
                )}
              </div>
              <p className="text-sm font-semibold text-foreground">{s.title}</p>
              <p className="text-xs text-muted-foreground">{s.desc}</p>
              {next ? (
                <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-primary">
                  Do this next <ArrowRight className="h-3 w-3" />
                </span>
              ) : (
                !complete && (
                  <span className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-primary opacity-0 transition-opacity group-hover:opacity-100">
                    Go <ArrowRight className="h-3 w-3" />
                  </span>
                )
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
